import { useEffect, useRef, useState } from "react";
import { enqueuedPost } from "@/lib/offlineQueue";

/**
 * useDriverGps — watches browser geolocation while mission is active and pings backend.
 * Enabled when {enabled, missionId} are both truthy.
 * Returns { lastPing, error, enabled }.
 */
export default function useDriverGps({ enabled, missionId }) {
  const [lastPing, setLastPing] = useState(null);
  const [error, setError] = useState(null);
  const lastSentRef = useRef(0);

  useEffect(() => {
    if (!enabled || !missionId) return;
    if (!("geolocation" in navigator)) {
      setError("Géolocalisation non disponible sur ce navigateur");
      return;
    }

    const onSuccess = async (pos) => {
      const now = Date.now();
      // Throttle: one ping every 5s
      if (now - lastSentRef.current < 5000) return;
      lastSentRef.current = now;
      const speed_kmh = pos.coords.speed && pos.coords.speed > 0 ? Math.round(pos.coords.speed * 3.6 * 10) / 10 : 0;
      const heading = pos.coords.heading ?? 0;
      try {
        const res = await enqueuedPost(`/missions/${missionId}/gps-ping`, {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          speed_kmh,
          heading: heading || 0,
        });
        setLastPing({ lat: pos.coords.latitude, lng: pos.coords.longitude, speed_kmh, at: new Date(), queued: !!res?.queued });
        setError(null);
      } catch (e) {
        setError(e?.message || "Erreur ping GPS");
      }
    };

    const onError = (e) => {
      setError(e?.message || "Position indisponible");
    };

    const watchId = navigator.geolocation.watchPosition(onSuccess, onError, {
      enableHighAccuracy: true,
      maximumAge: 5000,
      timeout: 15000,
    });

    return () => {
      navigator.geolocation.clearWatch(watchId);
    };
  }, [enabled, missionId]);

  return { lastPing, error, enabled: !!(enabled && missionId) };
}
